import './insightdashboard.css'

import React, { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react'
import {
	IonButton,
	IonBackButton,
	IonContent,
	IonIcon,
	IonPage,
	useIonRouter,
	useIonViewDidLeave,
	useIonViewWillEnter,
} from '@ionic/react'
import { Capacitor } from '@capacitor/core'
import { StatusBar } from '@capacitor/status-bar'
import { closeCircleOutline, informationCircleOutline, recordingOutline } from 'ionicons/icons'
import { useLocation, useParams } from 'react-router-dom'

import {
	useMyGame,
	useMyFlowForGame,
	useMyGames,
	useMyNumPeriods,
	useSection,
	useMyTeam,
} from './data'
import {
	FlowMapChain,
	FlowMapChainBadge,
	FlowMapDashboardData,
	FlowMapParsedRow,
	FlowMapViewId,
	deriveFlowMapDashboardData,
	flowDataToParsedRows,
} from './flowMap'
import LoadingSpinner from './LoadingSpinner'
import { pushOrGoBackToPreviousPage } from './navigation'
import { formatPeriodNumber } from './periodLabels'
import { useWakeLock } from './useWakeLock'
import Icon from './Icon'
import { getUrl } from './util'
import {
	HelpInsightSectionContent,
	type HelpInsightSection,
} from './Help'
import { MoreInfoLink } from './MoreInfoLink'
import { isFlowGameComplete } from './flowProgress'
import ModalWithCancel from './ModalWithCancel'
import ShareButton from './ShareButton'
import { getGameSharePreviewUrl } from './previewShareUrl'

const FLOW_SECTION_HELP_TITLES: Record<HelpInsightSection, string> = {
	scoring: 'Scoring',
	dominance: 'Dominance',
	chains: 'Chains',
	'conversion-efficiency': 'Conversion Efficiency',
    map: 'Map',
}

const enterFlowMapFullscreen = () => {
	document.body.classList.add('flow-map-fullscreen')
}

const leaveFlowMapFullscreen = () => {
	document.body.classList.remove('flow-map-fullscreen')
}

const clearFlowMapFullscreen = () => {
	leaveFlowMapFullscreen()
	document.body.style.removeProperty('--flow-status-bar-height')
}

const InsightDashboard = () => {

	const sectionGame = useMyGame()
	const games = useMyGames()
	const team = useMyTeam()
	const numPeriods = useMyNumPeriods()
	const section = useSection()
	const router = useIonRouter()
	const { game: routeGameId } = useParams<{ game: string }>()
	const location = useLocation()
	const routeSectionParams = new URLSearchParams(location.search)
	const routeTeamId = routeSectionParams.get('team')
	const routeSeasonId = routeSectionParams.get('season')
	const sectionMatchesRoute = (!routeTeamId || routeTeamId === section.team)
		&& (!routeSeasonId || routeSeasonId === section.season)
	const gameId = routeGameId || sectionGame?.id
	const routeFlow = useMyFlowForGame(gameId)
	const flow = routeFlow.data?.id === gameId ? routeFlow.data : null
	const flowIsLoading = !routeFlow.loaded
	const game = sectionMatchesRoute && gameId
		? games?.[gameId] || (sectionGame?.id === gameId ? sectionGame : null)
		: null
	const hasGameContext = sectionMatchesRoute && !!gameId && !!section.team && !!section.season
	const gameIsLoading = !sectionMatchesRoute || (hasGameContext && games === null)
	const gameDoesNotExist = !!gameId
		&& sectionMatchesRoute
		&& (
			!section.team
			|| !section.season
			|| (games !== null && !game)
		)
	const backHref = `/games${location.search || ''}`
	const shareUrl = gameId ? getGameSharePreviewUrl({
		kind: 'flow-map',
		gameId,
		section,
	}) : undefined
	const gameUrl = gameId ? getUrl({
		relative: true,
		page: `games/${gameId}`,
		section,
	}) : null
	const recordFlowUrl = gameId ? getUrl({
		relative: true,
		page: `games/${gameId}/record-flows`,
		section,
	}) : null
	const recordFlowSetupUrl = recordFlowUrl
		? `${recordFlowUrl}${recordFlowUrl.includes('?') ? '&' : '?'}setup=1`
		: null
	const hasDashboardData = !!flow?.events?.length
	const shouldKeepScreenAwake = !!game && !isFlowGameComplete(flow, numPeriods)

	useWakeLock(shouldKeepScreenAwake)

	useIonViewWillEnter(() => {
		enterFlowMapFullscreen()
	})

	useIonViewDidLeave(() => {
		clearFlowMapFullscreen()
	})

	useEffect(() => {
		enterFlowMapFullscreen()
		let cancelled = false

		const applyStatusBarInset = async () => {
			if (Capacitor.getPlatform() === 'web') {
				document.body.style.removeProperty('--flow-status-bar-height')
				return
			}

			try {
				const info = await StatusBar.getInfo()
				if (!cancelled) {
					document.body.style.setProperty(
						'--flow-status-bar-height',
						`${info.overlays ? info.height : 0}px`,
					)
				}
			} catch (error) {
				console.warn('Unable to read status bar height for flow map safe area.', error)
			}
		}

		void applyStatusBarInset()
		const handleResize = () => {
			void applyStatusBarInset()
		}
		window.addEventListener('resize', handleResize)

		return () => {
			cancelled = true
			clearFlowMapFullscreen()
			window.removeEventListener('resize', handleResize)
		}
	}, [ ])

	const dashboard = useMemo(() => {
		if (hasDashboardData && flow) {
			return deriveFlowMapDashboardData({
				parsedRows: flowDataToParsedRows(flow, team?.name || 'Home', game?.opponent || 'Away'),
				homeName: team?.name,
				awayName: game?.opponent,
				result: game?.result,
				numPeriods,
				teamColors: {
					home: flow.setup.homeColor,
					away: flow.setup.awayColor,
				},
			})
		}
		return deriveFlowMapDashboardData({
			parsedRows: {
				rows: [ ],
				codes: [ 'H', 'A' ],
				quarterNumbers: [ ],
				homeCode: 'H',
				awayCode: 'A',
			},
			homeName: team?.name,
			awayName: game?.opponent,
			result: game?.result,
			numPeriods,
		})
	}, [
		flow,
		game?.opponent,
		game?.result,
		hasDashboardData,
		numPeriods,
		team?.name,
	])

	const dashboardStyle = flow?.setup ? ({
		'--flow-home-color-override': flow.setup.homeColor,
		'--flow-away-color-override': flow.setup.awayColor,
	} as React.CSSProperties) : undefined

	const defaultViewId = dashboard?.defaultViewId || 'full'
	const [ selectedViewId, setSelectedViewId ] = useState<FlowMapViewId>(defaultViewId)
	const [ flipped, setFlipped ] = useState(false)
	const [ isMapExpanded, setIsMapExpanded ] = useState(false)
	const [ isScoreSequenceExpanded, setIsScoreSequenceExpanded ] = useState(false)
	const [ activeHelpSection, setActiveHelpSection ] = useState<HelpInsightSection | null>(null)

	useEffect(() => {
		setSelectedViewId(defaultViewId)
	}, [ defaultViewId, gameId ])

	useEffect(() => {
		if (!dashboard) {
			return
		}
		const selectedView = dashboard.views[selectedViewId]
		if (!selectedView) {
			setSelectedViewId(dashboard.defaultViewId)
		}
	}, [ dashboard, selectedViewId ])

	if (gameDoesNotExist) {
		return (
			<IonPage>
				<IonContent>
					<div className="flow-map-dashboard" style={dashboardStyle}>
						<IonBackButton className="flow-fullscreen-exit" defaultHref={backHref} icon={closeCircleOutline} text="Exit" />
						<section className="flow-card">
							<div className="flow-card-body">
								<h1>This game does not exist.</h1>
							</div>
						</section>
					</div>
				</IonContent>
			</IonPage>
		)
	}

	if (gameIsLoading || (!hasDashboardData && flowIsLoading)) {
		return (
			<IonPage>
				<IonContent>
					<div className="flow-map-dashboard" style={dashboardStyle}>
						<IonBackButton className="flow-fullscreen-exit" defaultHref={backHref} icon={closeCircleOutline} text="Exit" />
                        <LoadingSpinner />
					</div>
				</IonContent>
			</IonPage>
		)
	}

	const selectedView = dashboard?.views[selectedViewId] || dashboard?.views[dashboard.defaultViewId]
	if (!dashboard || !selectedView) {
		return (
			<IonPage>
				<IonContent>
					<div className="flow-map-dashboard" style={dashboardStyle}>
						<IonBackButton className="flow-fullscreen-exit" defaultHref={backHref} icon={closeCircleOutline} text="Exit" />
					</div>
				</IonContent>
			</IonPage>
		)
	}

	const hasSuperGoals = (
		dashboard.views.full.overview.superGoals.home +
		dashboard.views.full.overview.superGoals.away
	) > 0
	const isLiveFlowInProgress = !!flow && flow.currentQuarter <= numPeriods
	const closeHelpModal = (show: boolean) => {
		if (!show) {
			setActiveHelpSection(null)
		}
	}

	return (
		<IonPage>
			<IonContent>
				<div
					className={`flow-map-dashboard${isScoreSequenceExpanded ? ' scoreworm-expanded' : ''}`}
					style={dashboardStyle}
				>
					<div className="flow-sticky">
						<section className="flow-card flow-sticky-card">
							<div className="flow-sticky-score">
								<div className="flow-sticky-team">
									<div className="flow-sticky-team-name">
										{dashboard.teams.home.name}
									</div>
								</div>
								<div className="flow-sticky-main">
									<div className="flow-sticky-numbers">
										<span className="flow-sticky-score-value home">
											{selectedView.score.home}
										</span>
										<span className="flow-sticky-separator">
											-
										</span>
										<span className="flow-sticky-score-value away">
											{selectedView.score.away}
										</span>
									</div>
								</div>
								<div className="flow-sticky-team">
									<div className="flow-sticky-team-name">
										{dashboard.teams.away.name}
									</div>
								</div>
							</div>
						</section>

						<nav className="flow-segment" aria-label="Flow Map period selector">
							<SegmentButton
								label="Full Game"
								isActive={selectedViewId === 'full'}
								onClick={() => setSelectedViewId('full')}
							/>
							{
								dashboard.quarterNumbers.map(quarter => {
									const viewId = `q${quarter}` as const
									const view = dashboard.views[viewId]
									return (
										<SegmentButton
											key={viewId}
											label={view?.buttonLabel || formatPeriodNumber(quarter, numPeriods)}
											isActive={selectedViewId === viewId}
											isDisabled={!view?.hasData}
											onClick={() => setSelectedViewId(viewId)}
										/>
									)
								})
							}
						</nav>

					</div>

                    <section className="flow-header">
                        <div className="flow-header-left-buttons">
                            <IonButton
                                className="flow-header-settings"
                                fill="clear"
                                color="dark"
                                disabled={!recordFlowSetupUrl}
                                onClick={() => {
                                    if (recordFlowSetupUrl) {
                                        pushOrGoBackToPreviousPage(router, recordFlowSetupUrl)
                                    }
                                }}
                            >
                                <IonIcon icon={recordingOutline} />
                            </IonButton>
							<ShareButton url={shareUrl} shareOnly={true} />
                            <MoreInfoLink text="" helpSection="insight" routerDirection="none" />
                        </div>
                        <a
							className="flow-brand-title"
							href={gameUrl || undefined}
							onClick={event => {
								if (
									!gameUrl
									|| event.button !== 0
									|| event.ctrlKey
									|| event.metaKey
									|| event.shiftKey
									|| event.altKey
								) {
									return
								}
								event.preventDefault()
								pushOrGoBackToPreviousPage(router, gameUrl)
							}}
						>
                            <Icon />
                            <div>
                                AutoRoster
                            </div>
                            <div className="flow-brand-subtitle">
                                Flow Map
                            </div>
                        </a>
                        <IonBackButton className="flow-fullscreen-exit" defaultHref={backHref} icon={closeCircleOutline} text="Exit" />
                    </section>

					<div
						className={`flow-card-scoring-sequence${isScoreSequenceExpanded ? ' expanded' : ''}`}
						role="button"
						tabIndex={0}
						aria-expanded={isScoreSequenceExpanded}
						onClick={() => setIsScoreSequenceExpanded(value => !value)}
						onKeyDown={event => {
							if (event.key === 'Enter' || event.key === ' ') {
								event.preventDefault()
								setIsScoreSequenceExpanded(value => !value)
							}
						}}
					>
                        <section className="flow-card">
                            <div className="flow-card-body">
                                <div className="flow-section-label flow-section-label-scoreworm">
                                    Scoring
									<FlowSectionHelpIcon
										helpSection="scoring"
										onHelpClick={setActiveHelpSection}
									/>
                                </div>

                                <ScoringSequence
                                    dashboard={dashboard}
                                    selectedViewId={selectedViewId}
                                    fillWidth={!isLiveFlowInProgress && hasDashboardData}
                                    liveCurrentQuarter={isLiveFlowInProgress ? flow.currentQuarter : null}
									onHelpClick={setActiveHelpSection}
                                />
                            </div>
                        </section>

                    </div>

                    <div className={`flow-main${isMapExpanded ? ' map-expanded' : ''}`}>

                        <section
							className={`flow-map${isMapExpanded ? ' expanded' : ''}`}
							onClick={() => setIsMapExpanded(value => !value)}
						>
                            <FlowSectionHelpIcon
                                helpSection="map"
                                onHelpClick={setActiveHelpSection}
                            />

                            <FlowCourt
                                dashboard={dashboard}
                                viewId={selectedViewId}
                                flipped={flipped}
                            />
                            <button
                                type="button"
                                className="flow-flip-button"
                                onClick={event => {
									event.stopPropagation()
									setFlipped(f => !f)
								}}
                                title={flipped ? 'Flip: away attacks left' : 'Flip: home attacks right'}
                            >
                                Flip Court ⇄
                            </button>

                            {
                                (selectedView.flow.connectors.hiddenHomeOffensiveGains > 0 || selectedView.flow.connectors.hiddenAwayOffensiveGains > 0) && (
                                    <div className="flow-map-note">
                                        Extra offensive gains are shown beside the midcourt arrows and sit outside the main court flow:
                                        {selectedView.flow.connectors.hiddenHomeOffensiveGains > 0 && (
                                            <span className="home">
                                                {' '}
                                                {dashboard.teams.home.code}+{selectedView.flow.connectors.hiddenHomeOffensiveGains}
                                            </span>
                                        )}
                                        {selectedView.flow.connectors.hiddenAwayOffensiveGains > 0 && (
                                            <span className="away">
                                                {' '}
                                                {dashboard.teams.away.code}+{selectedView.flow.connectors.hiddenAwayOffensiveGains}
                                            </span>
                                        )}
                                    </div>
                                )
                            }
                        </section>

                        <section className="overview-rows">
                            <OverviewRow
                                label="Attack Entries"
                                home={selectedView.overview.entriesIntoAttack.home}
                                away={selectedView.overview.entriesIntoAttack.away}
                            />
                            <OverviewRow
                                label="Midcourt Entries"
                                home={selectedView.efficiency.midToAttack.home.from}
                                away={selectedView.efficiency.midToAttack.away.from}
                            />
                            <OverviewRow
                                label="Scores from Centre Passes"
                                home={selectedView.overview.cpScores.home}
                                away={selectedView.overview.cpScores.away}
                            />
                            <OverviewRow
                                label="Attack Gains"
                                home={selectedView.overview.offensiveGains.home}
                                away={selectedView.overview.offensiveGains.away}
                            />
                            <OverviewRow
                                label="Midcourt Gains"
                                home={selectedView.overview.gains.home.midcourt}
                                away={selectedView.overview.gains.away.midcourt}
                            />
                            <OverviewRow
                                label="Defense Gains"
                                home={selectedView.overview.gains.home.defensive}
                                away={selectedView.overview.gains.away.defensive}
                                groupStart={true}
                            />
                            {
                                hasSuperGoals && (
                                    <OverviewRow
                                        label="Super Goals"
                                        home={selectedView.overview.superGoals.home}
                                        away={selectedView.overview.superGoals.away}
                                    />
                                )
                            }
                        </section>
                    </div>

                    <div className="flow-grid">
                        <EfficiencyTable
                            title="Midcourt"
                            type="midToAttack"
                            dashboard={dashboard}
                            selectedViewId={selectedViewId}
							onHelpClick={setActiveHelpSection}
                        />
                        <EfficiencyTable
                            title="Attack"
							type="attackToScore"
							dashboard={dashboard}
							selectedViewId={selectedViewId}
							onHelpClick={setActiveHelpSection}
						/>
					</div>

					<ScoreChains
						dashboard={dashboard}
						selectedViewId={selectedViewId}
						enableLiveAnimations={isLiveFlowInProgress}
						onHelpClick={setActiveHelpSection}
					/>
					<ModalWithCancel
						show={!!activeHelpSection}
						setShow={closeHelpModal}
						title="Help"
						cancelText="Close"
						className="flow-help-modal"
					>
						<div className="flow-help-modal-content">
							{activeHelpSection && (
								<HelpInsightSectionContent section={activeHelpSection} />
							)}
						</div>
					</ModalWithCancel>
				</div>
			</IonContent>
		</IonPage>
	)
}

const SegmentButton = (props: {
	label: string
	isActive: boolean
	isDisabled?: boolean
	onClick: () => void
}) => (
	<button
		type="button"
		className={[
			'flow-segment-button',
			props.isActive ? 'active' : '',
			props.isDisabled ? 'disabled' : '',
		].filter(Boolean).join(' ')}
		onClick={props.onClick}
		disabled={props.isDisabled}
	>
		{props.label}
	</button>
)

const FlowSectionHelpIcon = (props: {
	helpSection: HelpInsightSection
	onHelpClick: (section: HelpInsightSection) => void
}) => {
	const title = FLOW_SECTION_HELP_TITLES[props.helpSection]

	return (
		<button
			type="button"
			className="flow-section-help-button"
			aria-label={`About ${title}`}
			onClick={event => {
				event.stopPropagation()
				props.onHelpClick(props.helpSection)
			}}
			onKeyDown={event => event.stopPropagation()}
		>
			<IonIcon
				className="more-info-link-icon flow-section-help-icon"
				icon={informationCircleOutline}
			/>
		</button>
	)
}

const ScoringSequence = (props: {
	dashboard: FlowMapDashboardData
	selectedViewId: FlowMapViewId
	fillWidth: boolean
	liveCurrentQuarter: number | null
	onHelpClick: (section: HelpInsightSection) => void
}) => {
	const { dashboard, selectedViewId, fillWidth, liveCurrentQuarter, onHelpClick } = props

	const sequence = useMemo(() => buildSequenceLayout(dashboard), [ dashboard ])
	const clipId = useId().replace(/:/g, '')
	const tramlineGradientBaseId = useId().replace(/:/g, '')
	const selectedView = dashboard.views[selectedViewId] || dashboard.views.full
	const quartersWithData = dashboard.quarterNumbers.filter(q =>
		dashboard.cps.some(cp => cp.quarter === q && cp.scorer),
	).length
	const totalQuarters = dashboard.quarterNumbers.length
	const scoredProjection = quartersWithData > 0
		? (sequence.endX * totalQuarters / quartersWithData)
		: sequence.endX
	const liveCompletedQuarters = liveCurrentQuarter
		? Math.max(0, Math.min(liveCurrentQuarter - 1, totalQuarters))
		: 0
	let completedPeriodProjection = 0
	if (liveCompletedQuarters > 0) {
		let completedEndX = 0
		sequence.quarterEndX.forEach((endX, quarter) => {
			if (quarter <= liveCompletedQuarters) {
				completedEndX = Math.max(completedEndX, endX)
			}
		})
		completedPeriodProjection = completedEndX * totalQuarters / liveCompletedQuarters
	} else if (liveCurrentQuarter && sequence.endX > 0) {
		completedPeriodProjection = sequence.endX * totalQuarters
	}
	const projectedFullWidth = Math.max(scoredProjection, completedPeriodProjection)
	const totalWidth = Math.max(sequence.endX + 10, projectedFullWidth + 10)
	const totalSvgWidth = totalWidth + SEQUENCE_TRAMLINE_LABEL_GUTTER_LEFT + SEQUENCE_TRAMLINE_LABEL_GUTTER_RIGHT
	const shouldFillAvailableWidth = fillWidth || liveCompletedQuarters > 0
	const stackStyle = shouldFillAvailableWidth
		? undefined
		: { width: `min(${totalSvgWidth}px, 100%)` }
	const viewBoxFor = (top: number, height: number) =>
		`${-SEQUENCE_TRAMLINE_LABEL_GUTTER_LEFT} ${top} ${totalSvgWidth} ${height}`
	const chartTone = scoreTone(selectedView.score.home, selectedView.score.away)
	const activeWindow = selectedViewId === 'full'
		? {
			startX: 0,
			endX: totalWidth,
		}
		: (sequence.windows[selectedViewId] || {
			startX: 0,
			endX: sequence.endX,
		})
	const isQuarterView = selectedViewId !== 'full'
	const isDimmedView = (viewId: FlowMapViewId) => selectedViewId !== 'full' && selectedViewId !== viewId

	return (
		<div
			className={`flow-sequence-stack${shouldFillAvailableWidth ? ' fill-width' : ''}`}
			style={stackStyle}
		>
			<svg
				className="flow-sequence-svg scoreworm"
				viewBox={viewBoxFor(0, SEQUENCE_SCOREWORM_SVG_HEIGHT)}
				preserveAspectRatio="none"
			>
				<defs>
					<clipPath id={clipId}>
						<rect
							className="diff-clip-window"
							x={activeWindow.startX}
							y="0"
							width={Math.max(0, activeWindow.endX - activeWindow.startX)}
							height={SEQUENCE_SCOREWORM_SVG_HEIGHT}
						/>
					</clipPath>
				</defs>
				<line
					x1="0"
					y1={sequence.zeroY}
					x2={totalWidth}
					y2={sequence.zeroY}
					className="zero-line"
				/>

				{
					sequence.quarters.map(({ quarter, label, separatorX }) => (
						<React.Fragment key={quarter}>
							{
								separatorX !== null && (
									<line
										x1={separatorX}
										y1="14"
										x2={separatorX}
										y2="138"
										className="quarter-line"
									/>
								)
							}
							<text x={label.x} y="142" className="quarter-label">
								{label.text}
							</text>
						</React.Fragment>
					))
				}

				<path
					d={sequence.fillPath}
					className={`diff-fill ${chartTone} base ${isQuarterView ? 'dim' : 'hidden'}`}
				/>
				<path
					d={sequence.linePath}
					className={`diff-line ${chartTone} base ${isQuarterView ? 'dim' : 'hidden'}`}
				/>
				<path
					d={sequence.fillPath}
					className={`diff-fill ${chartTone} highlight`}
					clipPath={`url(#${clipId})`}
				/>
				<path
					d={sequence.linePath}
					className={`diff-line ${chartTone} highlight`}
					clipPath={`url(#${clipId})`}
				/>
			</svg>

			<svg
				className="flow-sequence-svg score-bars"
				viewBox={viewBoxFor(SEQUENCE_SCORE_BARS_TOP, SEQUENCE_SCORE_BARS_HEIGHT)}
				preserveAspectRatio="none"
			>
				{
					sequence.quarters.map(({ quarter, separatorX }) => (
						separatorX !== null && (
							<line
								key={quarter}
								x1={separatorX}
								y1={SEQUENCE_SCORE_BARS_TOP}
								x2={separatorX}
								y2={SEQUENCE_SCORE_BARS_TOP + SEQUENCE_SCORE_BARS_HEIGHT}
								className="quarter-line top"
							/>
						)
					))
				}
				{
					sequence.bars.map(bar => (
						<rect
							key={bar.key}
							x={bar.x}
							y={bar.y}
							width={bar.width}
							height={bar.height}
							rx={2}
							className={[
								'score-bar',
								bar.scorer,
								selectedViewId !== 'full' && selectedViewId !== bar.viewId ? 'dim' : '',
								bar.isSuper ? 'super' : '',
							].filter(Boolean).join(' ')}
						/>
					))
				}
			</svg>

            <div className="flow-section-label flow-section-label-tramlines">
                Dominance
				<FlowSectionHelpIcon
					helpSection="dominance"
					onHelpClick={onHelpClick}
				/>
            </div>

			<svg
				className="flow-sequence-svg tramlines"
				viewBox={viewBoxFor(SEQUENCE_TRAMLINE_TOP, SEQUENCE_TRAMLINE_HEIGHT)}
				preserveAspectRatio="none"
			>
				<defs>
					{
						sequence.tramlineGradientRects.map(rect => (
							<linearGradient
								key={rect.key}
								id={`${tramlineGradientBaseId}-${rect.key}`}
								x1={rect.x}
								y1="0"
								x2={rect.x + rect.width}
								y2="0"
								gradientUnits="userSpaceOnUse"
							>
								{
									rect.stops.map((stop, stopIndex) => (
										<stop
											key={`${rect.key}-${stopIndex}`}
											offset={`${stop.offset * 100}%`}
											stopColor={tramlineColor(stop.dominance)}
											stopOpacity={tramlineOpacity(stop.dominance)}
										/>
									))
								}
							</linearGradient>
						))
					}
				</defs>
				{
					sequence.quarters.map(({ quarter, label, separatorX }) => (
						<React.Fragment key={quarter}>
							{
								separatorX !== null && (
									<line
										x1={separatorX}
										y1={SEQUENCE_TRAMLINE_TOP}
										x2={separatorX}
										y2={SEQUENCE_SVG_HEIGHT - 6}
										className="quarter-line top"
									/>
								)
							}
							<text
								x={label.x}
								y="195"
								className={[
									'quarter-label',
									isDimmedView(`q${quarter}` as const) ? 'dim' : '',
								].filter(Boolean).join(' ')}
								aria-hidden="true"
							>
								{label.text}
							</text>
						</React.Fragment>
					))
				}
				<g className="flow-tramlines" aria-hidden="true">
					{
						sequence.tramlineGradientRects.map(rect => (
							<rect
								key={rect.key}
								x={rect.x}
								y={rect.y}
								width={rect.width}
								height={rect.height}
								className={[
									'flow-tramline',
									isDimmedView(rect.viewId) ? 'dim' : '',
								].filter(Boolean).join(' ')}
								fill={`url(#${tramlineGradientBaseId}-${rect.key})`}
							/>
						))
					}
					{
						sequence.tramlines.map(line => (
							<React.Fragment key={`label-${line.zone}`}>
								<text
									x="-6"
									y={line.y}
									className="flow-tramline-label left"
								>
									{tramlineZoneLabel(line.zone)}
								</text>
								<text
									x={totalWidth - 4}
									y={line.y}
									className="flow-tramline-label right"
								>
									{tramlineZoneLabel(line.zone)}
								</text>
							</React.Fragment>
						))
					}
				</g>
			</svg>
		</div>
	)
}

const OverviewRow = (props: {
	label: string
	home: number
	away: number
	groupStart?: boolean
}) => {
	const maxValue = Math.max(props.home, props.away, 1)

	return (
		<div className={['flow-overview-group', props.groupStart ? 'group-start' : ''].filter(Boolean).join(' ')}>
			<div className="flow-overview-label">
				{props.label}
			</div>
			<div className="flow-overview-row">
				<div className="flow-overview-value home">
					{props.home}
				</div>
				<div className="flow-overview-bars">
					<div
						className="flow-overview-bar home"
						style={{ height: `${Math.max(8, Math.round((props.home / maxValue) * 100))}%` }}
					/>
					<div
						className="flow-overview-bar away"
						style={{ height: `${Math.max(8, Math.round((props.away / maxValue) * 100))}%` }}
					/>
				</div>
				<div className="flow-overview-value away">
					{props.away}
				</div>
			</div>
		</div>
	)
}

const FlowCourt = (props: {
	dashboard: FlowMapDashboardData
	viewId: FlowMapViewId
	flipped: boolean
}) => {
	const [ flipAnimationClass, setFlipAnimationClass ] = useState('')
	const previousFlippedRef = useRef(props.flipped)
	const shadeGradientBaseId = useId().replace(/:/g, '')
	const view = props.dashboard.views[props.viewId]
	const animatedFlow = useAnimatedNumberTree<FlowCourtAnimatedFlow>(view.flow)
	const courtLeft = 40
	const courtTop = 40
	const courtWidth = 660
	const courtHeight = 320
	const courtRight = courtLeft + courtWidth
	const courtBottom = courtTop + courtHeight
	const thirdWidth = courtWidth / 3
	const courtShadeHeight = courtHeight * 0.085
	const courtShadeY = courtBottom - courtShadeHeight
	const centreX = courtLeft + (courtWidth / 2)
	const centreY = courtTop + (courtHeight / 2)
	const goalCircleRadius = (courtHeight * 4.9) / 15.25
	const centreCircleRadius = (courtHeight * 0.45) / 15.25
	const goalArcTop = centreY - goalCircleRadius
	const goalArcBottom = centreY + goalCircleRadius
	const leftGoalArcPath = `M ${courtLeft} ${goalArcTop} A ${goalCircleRadius} ${goalCircleRadius} 0 0 1 ${courtLeft} ${goalArcBottom}`
	const rightGoalArcPath = `M ${courtRight} ${goalArcTop} A ${goalCircleRadius} ${goalCircleRadius} 0 0 0 ${courtRight} ${goalArcBottom}`
	const maxFlowWidthValue = Math.max(animatedFlow.homeMid, animatedFlow.awayMid, 1)
	const maxFlowThickness = 42
	const connectorOpacity = 0.57
	const homeCpHalfWidth = scaleFlowThickness(animatedFlow.homeCp, maxFlowWidthValue, maxFlowThickness, 12) / 2
	const awayCpHalfWidth = scaleFlowThickness(animatedFlow.awayCp, maxFlowWidthValue, maxFlowThickness, 12) / 2
	const homeMidHalfHeight = scaleFlowThickness(animatedFlow.homeMid, maxFlowWidthValue, maxFlowThickness, 12) / 2
	const awayMidHalfHeight = scaleFlowThickness(animatedFlow.awayMid, maxFlowWidthValue, maxFlowThickness, 12) / 2
	const homeAttackHalfWidth = scaleFlowThickness(animatedFlow.homeAttack, maxFlowWidthValue, maxFlowThickness, 12) / 2
	const awayAttackHalfWidth = scaleFlowThickness(animatedFlow.awayAttack, maxFlowWidthValue, maxFlowThickness, 12) / 2
	const homeOuterConnectorStroke = scaleFlowThickness(animatedFlow.connectors.awayDefenseToHomeMid, maxFlowWidthValue, maxFlowThickness, 3, 12)
	const awayOuterConnectorStroke = scaleFlowThickness(animatedFlow.connectors.homeAttackToAwayMid, maxFlowWidthValue, maxFlowThickness, 3, 12)
	const homeInnerConnectorStroke = scaleFlowThickness(animatedFlow.connectors.awayMidToHomeMid, maxFlowWidthValue, maxFlowThickness, 3, 12)
	const awayInnerConnectorStroke = scaleFlowThickness(animatedFlow.connectors.homeMidToAwayMid, maxFlowWidthValue, maxFlowThickness, 3, 12)
	const homeCpBaseY = centreY - centreCircleRadius - 7
	const awayCpBaseY = centreY + centreCircleRadius + 7
	const midBodyLength = 96
	const midHeadLength = 18
	const midBodyLeftX = centreX - (midBodyLength / 2)
	const midBodyRightX = centreX + (midBodyLength / 2)
	const homeMidTipX = midBodyLeftX - midHeadLength
	const awayMidTipX = midBodyRightX + midHeadLength
	const homeMidY = 118
	const awayMidY = 282
	const homeAttackStartX = 192
	const homeAttackStartY = 125
	const homeAttackEndX = 112
	const homeAttackEndY = 164
	const awayAttackStartX = 548
	const awayAttackStartY = 275
	const awayAttackEndX = 628
	const awayAttackEndY = 236
	const homeCpArrow = diagonalArrowShape({
		startX: 370,
		startY: homeCpBaseY,
		endX: 370,
		endY: 145,
		shaftHalfWidth: homeCpHalfWidth,
		headLength: 13,
	})
	const awayCpArrow = diagonalArrowShape({
		startX: 370,
		startY: awayCpBaseY,
		endX: 370,
		endY: 255,
		shaftHalfWidth: awayCpHalfWidth,
		headLength: 13,
	})
	const homeMidArrow = centeredHorizontalBlockArrowShape({
		centerX: centreX,
		centerY: homeMidY,
		bodyLength: midBodyLength,
		shaftHalfWidth: homeMidHalfHeight,
		headLength: midHeadLength,
		direction: 'left',
	})
	const awayMidArrow = centeredHorizontalBlockArrowShape({
		centerX: centreX,
		centerY: awayMidY,
		bodyLength: midBodyLength,
		shaftHalfWidth: awayMidHalfHeight,
		headLength: midHeadLength,
		direction: 'right',
	})
	const homeAttackArrow = diagonalArrowShape({
		startX: homeAttackStartX,
		startY: homeAttackStartY,
		endX: homeAttackEndX,
		endY: homeAttackEndY,
		shaftHalfWidth: homeAttackHalfWidth,
		headLength: 22,
	})
	const awayAttackArrow = diagonalArrowShape({
		startX: awayAttackStartX,
		startY: awayAttackStartY,
		endX: awayAttackEndX,
		endY: awayAttackEndY,
		shaftHalfWidth: awayAttackHalfWidth,
		headLength: 22,
	})
	const homeCpLabel = pointOnLine(370, homeCpBaseY, 370, 145, 0.38)
	const awayCpLabel = pointOnLine(370, awayCpBaseY, 370, 255, 0.38)
	const homeMidLabel = { x: centreX, y: homeMidY }
	const awayMidLabel = { x: centreX, y: awayMidY }
	const homeAttackLabel = pointOnLine(homeAttackStartX, homeAttackStartY, homeAttackEndX, homeAttackEndY, 0.46)
	const awayAttackLabel = pointOnLine(awayAttackStartX, awayAttackStartY, awayAttackEndX, awayAttackEndY, 0.46)
	const homeMidExtra = (view.flow.homeCp + view.flow.connectors.awayDefenseToHomeMid + view.flow.connectors.awayMidToHomeMid) - view.flow.homeMid
	const awayMidExtra = (view.flow.awayCp + view.flow.connectors.homeAttackToAwayMid + view.flow.connectors.homeMidToAwayMid) - view.flow.awayMid
	const goalBaseRadius = 26
	const goalMinRadius = 18
	const totalGoals = animatedFlow.homeGoals + animatedFlow.awayGoals
	const homeGoalRadius = scaleGoalCircleRadius(animatedFlow.homeGoals, totalGoals, goalBaseRadius, goalMinRadius)
	const awayGoalRadius = scaleGoalCircleRadius(animatedFlow.awayGoals, totalGoals, goalBaseRadius, goalMinRadius)
	const homeMidExtraLabel = useRetainedLabel(homeMidExtra > 0 ? `+${homeMidExtra}` : null, homeMidExtra > 0)
	const awayMidExtraLabel = useRetainedLabel(awayMidExtra > 0 ? `+${awayMidExtra}` : null, awayMidExtra > 0)
	const zoneDominance = computeCourtZoneDominance(view, props.dashboard.views.full)
	const homeShadeGradientId = `${shadeGradientBaseId}-home-zone-shade`
	const awayShadeGradientId = `${shadeGradientBaseId}-away-zone-shade`

	useLayoutEffect(() => {
		if (previousFlippedRef.current === props.flipped) {
			return
		}

		previousFlippedRef.current = props.flipped
		setFlipAnimationClass(props.flipped ? ' flip-to-reversed' : ' flip-to-default')
	}, [ props.flipped ])

    return (
		<div
			className={`flow-court-shell${flipAnimationClass}`}
			onAnimationEnd={() => setFlipAnimationClass('')}
		>
		<svg className="flow-court-svg" viewBox={`0 ${courtTop} 740 ${courtHeight}`}>
			<defs>
				<linearGradient id={homeShadeGradientId} x1="0" y1={courtShadeY} x2="0" y2={courtBottom} gradientUnits="userSpaceOnUse">
					<stop offset="0%" stopColor="var(--flow-home)" stopOpacity="0" />
					<stop offset="100%" stopColor="var(--flow-home)" stopOpacity="1" />
				</linearGradient>
				<linearGradient id={awayShadeGradientId} x1="0" y1={courtShadeY} x2="0" y2={courtBottom} gradientUnits="userSpaceOnUse">
					<stop offset="0%" stopColor="var(--flow-away)" stopOpacity="0" />
					<stop offset="100%" stopColor="var(--flow-away)" stopOpacity="1" />
				</linearGradient>
				<marker id="flow-offensive-gain-home" viewBox="0 0 6 6" refX="5.5" refY="3" markerWidth="4.5" markerHeight="4.5" orient="auto">
					<path d="M 0 0 L 6 3 L 0 6 z" fill="var(--flow-home)" />
				</marker>
				<marker id="flow-offensive-gain-away" viewBox="0 0 6 6" refX="5.5" refY="3" markerWidth="4.5" markerHeight="4.5" orient="auto">
					<path d="M 0 0 L 6 3 L 0 6 z" fill="var(--flow-away)" />
				</marker>
			</defs>

			<g className={`flow-court-field${props.flipped ? ' flipped' : ''}`}>
			<rect x={courtLeft} y={courtTop} width={courtWidth} height={courtHeight} rx="4" className="court-bg" />
			<CourtZoneShade
				x={courtLeft}
				y={courtShadeY}
				width={thirdWidth}
				height={courtShadeHeight}
				dominance={zoneDominance.attack}
				homeGradientId={homeShadeGradientId}
				awayGradientId={awayShadeGradientId}
			/>
			<CourtZoneShade
				x={courtLeft + thirdWidth}
				y={courtShadeY}
				width={thirdWidth}
				height={courtShadeHeight}
				dominance={zoneDominance.midcourt}
				homeGradientId={homeShadeGradientId}
				awayGradientId={awayShadeGradientId}
			/>
			<CourtZoneShade
				x={courtLeft + (thirdWidth * 2)}
				y={courtShadeY}
				width={thirdWidth}
				height={courtShadeHeight}
				dominance={zoneDominance.defence}
				homeGradientId={homeShadeGradientId}
				awayGradientId={awayShadeGradientId}
			/>
			<line x1={courtLeft + thirdWidth} y1={courtTop} x2={courtLeft + thirdWidth} y2={courtBottom} className="court-line" />
			<line x1={courtLeft + (thirdWidth * 2)} y1={courtTop} x2={courtLeft + (thirdWidth * 2)} y2={courtBottom} className="court-line" />
			<path d={leftGoalArcPath} className="court-line" />
			<path d={rightGoalArcPath} className="court-line" />
			<circle cx={centreX} cy={centreY} r={centreCircleRadius} className="court-line" />
			<FlipG x={courtLeft + (thirdWidth / 2)} flipped={props.flipped}>
				<text x={courtLeft + (thirdWidth / 2)} y="62" className="zone-label">
					{props.dashboard.teams.home.shortName} ATTACK
				</text>
			</FlipG>
			<FlipG x={centreX} flipped={props.flipped}>
				<text x={centreX} y="62" className="zone-label">
					MIDCOURT
				</text>
			</FlipG>
			<FlipG x={courtLeft + (thirdWidth * 2.5)} flipped={props.flipped}>
				<text x={courtLeft + (thirdWidth * 2.5)} y="62" className="zone-label">
					{props.dashboard.teams.home.shortName} DEFENCE
				</text>
			</FlipG>

			<Connector
				id="home-mid"
				count={view.flow.connectors.homeMidToAwayMid}
				path={`M ${homeMidTipX - 18},132 C 230,152 240,252 ${midBodyLeftX - 28},274`}
				className="away"
				labelX={278}
				labelY={200}
				isVisible={view.flow.connectors.homeMidToAwayMid > 0}
				strokeWidth={awayInnerConnectorStroke}
				opacity={connectorOpacity}
				flipped={props.flipped}
			/>
			<Connector
				id="home-attack"
				count={view.flow.connectors.homeAttackToAwayMid}
				path={`M ${homeAttackEndX - 20},176 C 74,198 170,300 ${midBodyLeftX - 28},290`}
				className="away"
				labelX={184}
				labelY={243}
				isVisible={view.flow.connectors.homeAttackToAwayMid > 0}
				strokeWidth={awayOuterConnectorStroke}
				opacity={connectorOpacity}
				flipped={props.flipped}
			/>
			<Connector
				id="away-defense"
				count={view.flow.connectors.awayDefenseToHomeMid}
				path={`M ${awayAttackEndX + 20},225 C 666,202 570,100 ${midBodyRightX + 28},110`}
				className="home"
				labelX={570}
				labelY={165}
				isVisible={view.flow.connectors.awayDefenseToHomeMid > 0}
				strokeWidth={homeOuterConnectorStroke}
				opacity={connectorOpacity}
				flipped={props.flipped}
			/>
			<Connector
				id="away-mid"
				count={view.flow.connectors.awayMidToHomeMid}
				path={`M ${awayMidTipX + 18},268 C 510,248 500,148 ${midBodyRightX + 28},126`}
				className="home"
				labelX={462}
				labelY={200}
				isVisible={view.flow.connectors.awayMidToHomeMid > 0}
				strokeWidth={homeInnerConnectorStroke}
				opacity={connectorOpacity}
				flipped={props.flipped}
			/>

			<polygon points={homeCpArrow.points} className="home-arrow" />
			<FlipG x={homeCpLabel.x} flipped={props.flipped}>
				<text x={homeCpLabel.x} y={homeCpLabel.y} className="arrow-label">
					{view.flow.homeCp}
				</text>
			</FlipG>
			<polygon points={homeMidArrow.points} className="home-arrow" />
			<FlipG x={homeMidLabel.x} flipped={props.flipped}>
				<text x={homeMidLabel.x} y={homeMidLabel.y} className="arrow-label">
					{view.flow.homeMid}
				</text>
			</FlipG>
			<FlipG x={centreX} flipped={props.flipped}>
				<text
					x={homeMidLabel.x + 18}
					y={homeMidLabel.y}
					className="arrow-extra-label flow-ball-element"
					textAnchor="end"
					style={{ opacity: homeMidExtra > 0 ? 0.7 : 0 }}
				>
					{homeMidExtraLabel}
				</text>
			</FlipG>
			<polygon points={homeAttackArrow.points} className="home-arrow" />
			<FlipG x={homeAttackLabel.x} flipped={props.flipped}>
				<text x={homeAttackLabel.x} y={homeAttackLabel.y} className="arrow-label">
					{view.flow.homeAttack}
				</text>
			</FlipG>
			<OffensiveGainBadge
				side="home"
				count={view.overview.offensiveGains.home}
				x={136}
				y={96}
				isVisible={view.overview.offensiveGains.home > 0}
				flipped={props.flipped}
			/>
			<circle cx={courtLeft} cy={centreY} r={homeGoalRadius} className="goal-circle home" />
			<FlipG x={courtLeft} flipped={props.flipped}>
				<text x={courtLeft} y={centreY} className="goal-label">
					{view.flow.homeGoals}
				</text>
			</FlipG>

			<polygon points={awayCpArrow.points} className="away-arrow" />
			<FlipG x={awayCpLabel.x} flipped={props.flipped}>
				<text x={awayCpLabel.x} y={awayCpLabel.y} className="arrow-label">
					{view.flow.awayCp}
				</text>
			</FlipG>
			<polygon points={awayMidArrow.points} className="away-arrow" />
			<FlipG x={awayMidLabel.x} flipped={props.flipped}>
				<text x={awayMidLabel.x} y={awayMidLabel.y} className="arrow-label">
					{view.flow.awayMid}
				</text>
			</FlipG>
			<FlipG x={centreX} flipped={props.flipped}>
				<text
					x={awayMidLabel.x + 18}
					y={awayMidLabel.y}
					className="arrow-extra-label flow-ball-element"
					style={{ opacity: awayMidExtra > 0 ? 0.7 : 0 }}
				>
					{awayMidExtraLabel}
				</text>
			</FlipG>
			<polygon points={awayAttackArrow.points} className="away-arrow" />
			<FlipG x={awayAttackLabel.x} flipped={props.flipped}>
				<text x={awayAttackLabel.x} y={awayAttackLabel.y} className="arrow-label">
					{view.flow.awayAttack}
				</text>
			</FlipG>
			<OffensiveGainBadge
				side="away"
				count={view.overview.offensiveGains.away}
				x={608}
				y={312}
				isVisible={view.overview.offensiveGains.away > 0}
				flipped={props.flipped}
			/>
			<circle cx={courtRight} cy={centreY} r={awayGoalRadius} className="goal-circle away" />
			<FlipG x={courtRight} flipped={props.flipped}>
				<text x={courtRight} y={centreY} className="goal-label">
					{view.flow.awayGoals}
				</text>
			</FlipG>
			</g>
		</svg>
		</div>
	)
}

const Connector = (props: {
	id: string
	count: number
	path: string
	className: FlowSideClass
	labelX: number
	labelY: number
	isVisible: boolean
	strokeWidth?: number
	opacity?: number
	flipped?: boolean
}) => {
	const strokeWidth = props.strokeWidth || 2.5
	const radius = 14
	const labelFontSize = 15
	const dashLength = Math.max(8, strokeWidth * 0.95)
	const gapLength = 12 // Math.max(5, strokeWidth * 0.62)
	const arrowHeadLength = Math.max(8, strokeWidth * 1.2)
	const arrowHalfWidth = Math.max(5, strokeWidth * 0.75)
	const { shortenedPath, arrowPoints } = connectorArrowhead(props.path, arrowHeadLength, arrowHalfWidth)
	const label = useRetainedLabel(props.count > 0 ? String(props.count) : null, props.isVisible)

	return (
		<g
			key={props.id}
			className="flow-ball-element"
			style={{ opacity: props.isVisible ? (props.opacity ?? 0.67) : 0 }}
		>
			<path
				d={shortenedPath}
				className={`flow-connector ${props.className}`}
				style={{
					strokeWidth,
					strokeDasharray: `${dashLength} ${gapLength}`,
				}}
			/>
			<polygon
				points={arrowPoints}
				className={`flow-connector-arrow ${props.className}`}
			/>
			<circle cx={props.labelX} cy={props.labelY} r={radius} className={`flow-connector-count ${props.className}`} />
			<FlipG x={props.labelX} flipped={!!props.flipped}>
				<text
					x={props.labelX}
					y={props.labelY}
					className="connector-label"
					style={{ fontSize: `${labelFontSize}px` }}
				>
					{label}
				</text>
			</FlipG>
		</g>
	)
}

const OffensiveGainBadge = (props: {
	side: FlowSideClass
	count: number
	x: number
	y: number
	isVisible: boolean
	flipped?: boolean
}) => {
	const marker = props.side === 'home'
		? 'url(#flow-offensive-gain-home)'
		: 'url(#flow-offensive-gain-away)'
	const label = useRetainedLabel(props.count > 0 ? String(props.count) : null, props.isVisible)

	return (
		<g
			className={`flow-offensive-gain ${props.side} flow-ball-element`}
			style={{ opacity: props.isVisible ? 1 : 0 }}
		>
			<path
				d={`M ${props.x - 13} ${props.y + 4} A 16 16 0 0 1 ${props.x + 4} ${props.y - 13}`}
				className={`flow-offensive-gain-ring ${props.side}`}
				markerEnd={marker}
			/>
			<path
				d={`M ${props.x + 13} ${props.y - 4} A 16 16 0 0 1 ${props.x - 4} ${props.y + 13}`}
				className={`flow-offensive-gain-ring ${props.side}`}
				markerEnd={marker}
			/>
			<circle cx={props.x} cy={props.y} r="10" className={`flow-offensive-gain-count ${props.side}`} />
			<FlipG x={props.x} flipped={!!props.flipped}>
				<text x={props.x} y={props.y} className="connector-label">
					{label}
				</text>
			</FlipG>
		</g>
	)
}

/** Wraps children in a counter-flip `<g>` so text reads normally inside a flipped SVG group. */
const FlipG = ({ x, flipped, children }: { x: number; flipped: boolean; children: React.ReactNode }) => {
	const style = {
		'--flow-counter-x': `${x}px`,
	} as React.CSSProperties & { '--flow-counter-x': string }

	return (
		<g className={`flow-counter-flip${flipped ? ' flipped' : ''}`} style={style}>
			{children}
		</g>
	)
}

type EfficiencyTableType = 'midToAttack' | 'attackToScore'
type FlowSideClass = 'home' | 'away'
type CourtZoneDominanceCell = {
	side: FlowSideClass | null
	strength: number
	saturation: number
}
type CourtZoneDominance = {
	attack: CourtZoneDominanceCell
	midcourt: CourtZoneDominanceCell
	defence: CourtZoneDominanceCell
}
type NumberTree = {
	[key: string]: number | NumberTree
}

type FlowCourtAnimatedFlow = {
	homeCp: number
	homeMid: number
	homeAttack: number
	homeGoals: number
	awayCp: number
	awayMid: number
	awayAttack: number
	awayGoals: number
	connectors: {
		homeMidToAwayMid: number
		homeAttackToAwayMid: number
		awayDefenseToHomeMid: number
		awayMidToHomeMid: number
		hiddenHomeOffensiveGains: number
		hiddenAwayOffensiveGains: number
	}
}

type EfficiencyTier = 'great' | 'high' | 'low' | 'bad'

const emptyCourtZoneDominance: CourtZoneDominanceCell = {
	side: null,
	strength: 0,
	saturation: 1,
}

const CourtZoneShade = (props: {
	x: number
	y: number
	width: number
	height: number
	dominance: CourtZoneDominanceCell
	homeGradientId: string
	awayGradientId: string
}) => {
	const fill = props.dominance.side === 'home'
		? `url(#${props.homeGradientId})`
		: `url(#${props.awayGradientId})`

	return (
		<rect
			x={props.x}
			y={props.y}
			width={props.width}
			height={props.height}
			className="court-zone-shade"
			fill={fill}
			style={{
				filter: `saturate(${props.dominance.saturation})`,
				opacity: props.dominance.side ? props.dominance.strength : 0,
			}}
		/>
	)
}

function computeCourtZoneDominance(
	view: FlowMapDashboardData['views'][FlowMapViewId],
	fullView: FlowMapDashboardData['views'][FlowMapViewId],
): CourtZoneDominance {
	const attackToScore = view.efficiency.attackToScore
	const fullAttackToScore = fullView.efficiency.attackToScore
	const midToAttack = view.efficiency.midToAttack
	const combinedAttackPct = pctFromTotals(
		fullAttackToScore.home.to + fullAttackToScore.away.to,
		fullAttackToScore.home.from + fullAttackToScore.away.from,
	)

	return {
		attack: combinedAttackPct === null
			? emptyCourtZoneDominance
			: dominanceFromDelta(
				(attackToScore.home.pct ?? combinedAttackPct) - combinedAttackPct,
				'home',
				'away',
				attackToScore.home.from,
			),
		midcourt: midToAttack.home.pct === null || midToAttack.away.pct === null
			? emptyCourtZoneDominance
			: dominanceFromDelta(
				midToAttack.home.pct - midToAttack.away.pct,
				'home',
				'away',
				Math.min(midToAttack.home.from, midToAttack.away.from),
			),
		defence: combinedAttackPct === null
			? emptyCourtZoneDominance
			: dominanceFromDelta(
				(attackToScore.away.pct ?? combinedAttackPct) - combinedAttackPct,
				'away',
				'home',
				attackToScore.away.from,
			),
	}
}

function pctFromTotals(to: number, from: number) {
	if (from <= 0) {
		return null
	}
	return Math.round((to / from) * 100)
}

function dominanceFromDelta(
	delta: number,
	positiveSide: FlowSideClass,
	negativeSide: FlowSideClass,
	sampleSize: number,
): CourtZoneDominanceCell {
	if (sampleSize <= 0) {
		return emptyCourtZoneDominance
	}

	const deadband = 4
	const fullStrengthDelta = 18
	const magnitude = Math.abs(delta)
	if (magnitude <= deadband) {
		return emptyCourtZoneDominance
	}

	return {
		side: delta > 0 ? positiveSide : negativeSide,
		saturation: Math.max(1, magnitude / fullStrengthDelta),
		strength: clamp((magnitude - deadband) / (fullStrengthDelta - deadband), 0, 1) * 0.72,
	}
}

function computeEfficiencyTiers(
	dashboard: FlowMapDashboardData,
	type: EfficiencyTableType,
): Map<string, EfficiencyTier> {
	const entries: Array<{ key: string; pct: number }> = []
	const sides: FlowSideClass[] = [ 'home', 'away' ]

	sides.forEach(side => {
		dashboard.quarterNumbers.forEach(quarter => {
			const viewId = `q${quarter}` as const
			const view = dashboard.views[viewId]
			if (view?.hasData) {
				const cell = view.efficiency[type][side]
				if (cell.pct !== null && cell.from > 0) {
					entries.push({ key: `${side}-${viewId}`, pct: cell.pct })
				}
			}
		})
		const fullCell = dashboard.views.full.efficiency[type][side]
		if (fullCell.pct !== null && fullCell.from > 0) {
			entries.push({ key: `${side}-full`, pct: fullCell.pct })
		}
	})

	if (entries.length < 3) {
		return new Map()
	}

	const mean = entries.reduce((sum, e) => sum + e.pct, 0) / entries.length
	const variance = entries.reduce((sum, e) => sum + (e.pct - mean) ** 2, 0) / entries.length
	const sd = Math.sqrt(variance)
	const sorted = entries.map(e => e.pct).sort((a, b) => a - b)
	const midLow = sorted[Math.floor(sorted.length / 3)]
	const midHigh = sorted[Math.floor((sorted.length * 2) / 3)]

	const tiers = new Map<string, EfficiencyTier>()
	entries.forEach(({ key, pct }) => {
		const z = sd > 0 ? (pct - mean) / sd : 0
		if (z < -1.5) {
			tiers.set(key, 'bad')
		} else if (z > 1.5) {
			tiers.set(key, 'great')
		} else if (pct < midLow) {
			tiers.set(key, 'low')
		} else if (pct >= midHigh) {
			tiers.set(key, 'high')
		}
	})

	return tiers
}

function scaleFlowThickness(
	value: number,
	maxValue: number,
	maxThickness: number,
	minThickness: number,
	dashedGapLength?: number,
) {
	if (!value) {
		return 0
	}
	if (!maxValue) {
		return minThickness
	}
	let thickness = maxThickness * Math.sqrt(value / maxValue)
	if (dashedGapLength) {
        return thickness * 0.5
	}
	return Math.max(minThickness, thickness)
}

function scaleGoalCircleRadius(
	value: number,
	total: number,
	baseRadius: number,
	minRadius: number,
) {
	if (!Number.isFinite(total) || total <= 0) {
		return baseRadius
	}
	if (!Number.isFinite(value) || value <= 0) {
		return minRadius
	}
	return Math.max(minRadius, baseRadius * Math.sqrt((2 * value) / total))
}

function useAnimatedNumberTree<T extends NumberTree>(target: T, duration = 280) {
	const [ animated, setAnimated ] = useState(target)
	const animatedRef = useRef(target)
	const targetRef = useRef(target)
	const frameRef = useRef<number | null>(null)

	useEffect(() => {
		animatedRef.current = animated
	}, [ animated ])

	useEffect(() => {
		if (numberTreeEquals(targetRef.current, target)) {
			return
		}

		targetRef.current = target
		if (frameRef.current !== null) {
			cancelAnimationFrame(frameRef.current)
		}

		const start = animatedRef.current
		const startedAt = performance.now()

		const tick = (now: number) => {
			const progress = Math.min(1, (now - startedAt) / duration)
			const eased = 1 - ((1 - progress) ** 3)
			const next = interpolateNumberTree(start, target, eased) as T
			animatedRef.current = next
			setAnimated(next)
			if (progress < 1) {
				frameRef.current = requestAnimationFrame(tick)
				return
			}
			frameRef.current = null
		}

		frameRef.current = requestAnimationFrame(tick)

		return () => {
			if (frameRef.current !== null) {
				cancelAnimationFrame(frameRef.current)
				frameRef.current = null
			}
		}
	}, [ duration, target ])

	useEffect(() => {
		return () => {
			if (frameRef.current !== null) {
				cancelAnimationFrame(frameRef.current)
			}
		}
	}, [])

	return animated
}

function useRetainedLabel(label: string | null, isVisible: boolean, duration = 280) {
	const [ retainedLabel, setRetainedLabel ] = useState(label)

	useEffect(() => {
		if (label && isVisible) {
			setRetainedLabel(label)
			return undefined
		}

		if (!isVisible) {
			const timeoutId = window.setTimeout(() => {
				setRetainedLabel(null)
			}, duration)
			return () => {
				window.clearTimeout(timeoutId)
			}
		}

		return undefined
	}, [ duration, isVisible, label ])

	return retainedLabel
}

function numberTreeEquals(left: NumberTree, right: NumberTree): boolean {
	const leftKeys = Object.keys(left)
	const rightKeys = Object.keys(right)
	if (leftKeys.length !== rightKeys.length) {
		return false
	}

	return leftKeys.every(key => {
		const leftValue = left[key]
		const rightValue = right[key]
		if (typeof leftValue === 'number' && typeof rightValue === 'number') {
			return Math.abs(leftValue - rightValue) < 0.001
		}
		if (typeof leftValue === 'number' || typeof rightValue === 'number') {
			return false
		}
		return numberTreeEquals(leftValue, rightValue)
	})
}

function interpolateNumberTree(start: NumberTree, end: NumberTree, progress: number): NumberTree {
	return Object.keys(end).reduce((tree, key) => {
		const startValue = start[key]
		const endValue = end[key]
		if (typeof startValue === 'number' && typeof endValue === 'number') {
			tree[key] = startValue + ((endValue - startValue) * progress)
			return tree
		}
		if (typeof startValue === 'number' || typeof endValue === 'number') {
			tree[key] = endValue
			return tree
		}
		tree[key] = interpolateNumberTree(startValue, endValue, progress)
		return tree
	}, { } as NumberTree)
}

function toSvgPoints(points: Array<{ x: number; y: number }>) {
	return points
		.map(point => `${safeSvgNumber(point.x).toFixed(1)},${safeSvgNumber(point.y).toFixed(1)}`)
		.join(' ')
}

function centeredHorizontalBlockArrowShape(props: {
	centerX: number
	centerY: number
	bodyLength: number
	shaftHalfWidth: number
	headLength: number
	direction: 'left' | 'right'
}) {
	const { centerX, centerY, bodyLength, shaftHalfWidth, headLength, direction } = props
	const bodyLeftX = centerX - (bodyLength / 2)
	const bodyRightX = centerX + (bodyLength / 2)
	const headHalfWidth = shaftHalfWidth * 1.42

	if (direction === 'right') {
		return {
			points: toSvgPoints([
				{ x: bodyLeftX, y: centerY - shaftHalfWidth },
				{ x: bodyRightX, y: centerY - shaftHalfWidth },
				{ x: bodyRightX, y: centerY - headHalfWidth },
				{ x: bodyRightX + headLength, y: centerY },
				{ x: bodyRightX, y: centerY + headHalfWidth },
				{ x: bodyRightX, y: centerY + shaftHalfWidth },
				{ x: bodyLeftX, y: centerY + shaftHalfWidth },
			]),
		}
	}

	return {
		points: toSvgPoints([
			{ x: bodyRightX, y: centerY - shaftHalfWidth },
			{ x: bodyLeftX, y: centerY - shaftHalfWidth },
			{ x: bodyLeftX, y: centerY - headHalfWidth },
			{ x: bodyLeftX - headLength, y: centerY },
			{ x: bodyLeftX, y: centerY + headHalfWidth },
			{ x: bodyLeftX, y: centerY + shaftHalfWidth },
			{ x: bodyRightX, y: centerY + shaftHalfWidth },
		]),
	}
}

function diagonalArrowShape(props: {
	startX: number
	startY: number
	endX: number
	endY: number
	shaftHalfWidth: number
	headLength: number
}) {
	const { startX, startY, endX, endY, shaftHalfWidth, headLength } = props
	const dx = endX - startX
	const dy = endY - startY
	const length = Math.hypot(dx, dy) || 1
	const ux = dx / length
	const uy = dy / length
	const px = -uy
	const py = ux
	const headBaseX = endX - (ux * headLength)
	const headBaseY = endY - (uy * headLength)
	const headHalfWidth = shaftHalfWidth * 1.42

	return {
		points: toSvgPoints([
			{ x: startX + (px * shaftHalfWidth), y: startY + (py * shaftHalfWidth) },
			{ x: headBaseX + (px * shaftHalfWidth), y: headBaseY + (py * shaftHalfWidth) },
			{ x: headBaseX + (px * headHalfWidth), y: headBaseY + (py * headHalfWidth) },
			{ x: endX, y: endY },
			{ x: headBaseX - (px * headHalfWidth), y: headBaseY - (py * headHalfWidth) },
			{ x: headBaseX - (px * shaftHalfWidth), y: headBaseY - (py * shaftHalfWidth) },
			{ x: startX - (px * shaftHalfWidth), y: startY - (py * shaftHalfWidth) },
		]),
	}
}

function connectorArrowhead(
	path: string,
	headLength: number,
	headHalfWidth: number,
) {
	const match = path.match(
		/M\s*([\d.-]+)[,\s]+([\d.-]+)\s*C\s*([\d.-]+)[,\s]+([\d.-]+)\s+([\d.-]+)[,\s]+([\d.-]+)\s+([\d.-]+)[,\s]+([\d.-]+)/
	)
	if (!match) {
		return { shortenedPath: path, arrowPoints: '' }
	}

	const cp2x = parseFloat(match[5])
	const cp2y = parseFloat(match[6])
	const endX = parseFloat(match[7])
	const endY = parseFloat(match[8])

	const dx = endX - cp2x
	const dy = endY - cp2y
	const len = Math.hypot(dx, dy) || 1
	const ux = dx / len
	const uy = dy / len
	const px = -uy
	const py = ux

	const baseX = endX - (ux * headLength)
	const baseY = endY - (uy * headLength)

	const shortenedPath = path.replace(
		/([\d.-]+)[,\s]+([\d.-]+)\s*$/,
		`${baseX.toFixed(1)},${baseY.toFixed(1)}`
	)

	const arrowPoints = toSvgPoints([
		{ x: endX, y: endY },
		{ x: baseX + (px * headHalfWidth), y: baseY + (py * headHalfWidth) },
		{ x: baseX - (px * headHalfWidth), y: baseY - (py * headHalfWidth) },
	])

	return { shortenedPath, arrowPoints }
}

function pointOnLine(x1: number, y1: number, x2: number, y2: number, t: number) {
	return {
		x: safeSvgNumber(x1 + ((x2 - x1) * t)),
		y: safeSvgNumber(y1 + ((y2 - y1) * t)),
	}
}

const EfficiencyTable = (props: {
	title: string
	type: EfficiencyTableType
	dashboard: FlowMapDashboardData
	selectedViewId: FlowMapViewId
	onHelpClick: (section: HelpInsightSection) => void
}) => {
	const tiers = useMemo(() => {
		return computeEfficiencyTiers(props.dashboard, props.type)
	}, [ props.dashboard, props.type ])

	return (
		<section className="flow-card">
			<div className="flow-card-body">
				<div className="flow-section-label">
					{props.title}
					<FlowSectionHelpIcon
						helpSection="conversion-efficiency"
						onHelpClick={props.onHelpClick}
					/>
				</div>
				<table className="flow-efficiency-table">
					<thead>
						<tr>
							<th />
							<th className={`eff-game-col${props.selectedViewId !== 'full' ? ' dim' : ''}`}>
								Game
							</th>
							{
								props.dashboard.quarterNumbers.map(quarter => (
									<th
										key={`q${quarter}`}
										className={props.selectedViewId !== 'full' && props.selectedViewId !== `q${quarter}` ? 'dim' : ''}
									>
										{props.dashboard.views[`q${quarter}`]?.buttonLabel || formatPeriodNumber(quarter, props.dashboard.quarterNumbers.length)}
									</th>
								))
							}
						</tr>
					</thead>
					<tbody>
						<EfficiencyTeamRow
							side="home"
							teamName={props.dashboard.teams.home.shortName}
							type={props.type}
							dashboard={props.dashboard}
							selectedViewId={props.selectedViewId}
							tiers={tiers}
						/>
						<EfficiencyTeamRow
							side="away"
							teamName={props.dashboard.teams.away.shortName}
							type={props.type}
							dashboard={props.dashboard}
							selectedViewId={props.selectedViewId}
							tiers={tiers}
						/>
					</tbody>
				</table>
			</div>
		</section>
	)
}

const EfficiencyTeamRow = (props: {
	side: FlowSideClass
	teamName: string
	type: EfficiencyTableType
	dashboard: FlowMapDashboardData
	selectedViewId: FlowMapViewId
	tiers: Map<string, EfficiencyTier>
}) => (
	<tr className={`flow-efficiency-row ${props.side}`}>
		<td className="flow-efficiency-team">
			{props.teamName}
		</td>
		<EfficiencyCell
			cell={props.dashboard.views.full.efficiency[props.type][props.side]}
			isDim={props.selectedViewId !== 'full'}
			tier={props.tiers.get(`${props.side}-full`)}
			isGame={true}
		/>
		{
			props.dashboard.quarterNumbers.map(quarter => {
				const viewId = `q${quarter}` as const
				const cell = props.dashboard.views[viewId].efficiency[props.type][props.side]
				return (
					<EfficiencyCell
						key={`${props.side}-${viewId}`}
						cell={cell}
						isDim={props.selectedViewId !== 'full' && props.selectedViewId !== viewId}
						tier={props.tiers.get(`${props.side}-${viewId}`)}
					/>
				)
			})
		}
	</tr>
)

const EfficiencyCell = (props: {
	cell: {
		from: number
		to: number
		pct: number | null
	}
	isDim: boolean
	tier?: EfficiencyTier
	isGame?: boolean
}) => (
	<td className={[
		props.isDim ? 'dim' : '',
		props.isGame ? 'eff-game-col' : '',
	].filter(Boolean).join(' ') || undefined}>
		<div className={`flow-efficiency-cell${props.tier ? ` eff-${props.tier}` : ''}`}>
			<span className="flow-efficiency-pct">
				{
					props.cell.pct === null ? '-' : (
						<>
							{props.cell.pct}
							<span className="flow-efficiency-pct-symbol">%</span>
						</>
					)
				}
			</span>
			<span className="flow-efficiency-raw">
				{props.cell.from}
				{' '}
				<span className="flow-efficiency-arrow">
					￫
				</span>
				{' '}
				{props.cell.to}
			</span>
		</div>
	</td>
)

function scoreTone(homeScore: number, awayScore: number) {
	if (homeScore > awayScore) {
		return 'home'
	}
	if (awayScore > homeScore) {
		return 'away'
	}
	return 'neutral'
}

function safeSvgNumber(value: number, fallback = 0) {
	return Number.isFinite(value) ? value : fallback
}

function clamp(value: number, min: number, max: number) {
	return Math.max(min, Math.min(max, value))
}

const SEQUENCE_PLOT_TOP = 16
const SEQUENCE_PLOT_BOTTOM = 136
const SEQUENCE_SVG_HEIGHT = 248
const SEQUENCE_SCOREWORM_SVG_HEIGHT = 146
const SEQUENCE_SCORE_BARS_TOP = 146
const SEQUENCE_SCORE_BARS_HEIGHT = 44
const SEQUENCE_TRAMLINE_TOP = 184
const SEQUENCE_TRAMLINE_HEIGHT = SEQUENCE_SVG_HEIGHT - SEQUENCE_TRAMLINE_TOP
const SEQUENCE_MIN_LEAD_SPAN = 4
const SEQUENCE_LEAD_PADDING = 0.5
const SEQUENCE_TRAMLINE_DECAY_HALF_LIFE = 3
const SEQUENCE_TRAMLINE_MIDCOURT_SIGNAL_DELTA = 24
const SEQUENCE_TRAMLINE_ATTACK_SIGNAL_DELTA = 16
const SEQUENCE_TRAMLINE_LABEL_GUTTER_LEFT = 14
const SEQUENCE_TRAMLINE_LABEL_GUTTER_RIGHT = 8
const SEQUENCE_TRAMLINE_THICKNESS = 12

type SequenceTramlineZone = 'attack' | 'midcourt' | 'defence'
type SequenceTramlineRelativeZone = 'defense' | 'mid' | 'attack'

type SequenceTramlineSample = {
	x: number
	zones: Record<SequenceTramlineZone, CourtZoneDominanceCell>
	viewId?: FlowMapViewId
	breakBefore?: boolean
}

type SequenceTramlineGradientStop = {
	offset: number
	dominance: CourtZoneDominanceCell
}

type SequenceTramlineGradientRect = {
	key: string
	zone: SequenceTramlineZone
	x: number
	y: number
	width: number
	height: number
	viewId: FlowMapViewId
	stops: SequenceTramlineGradientStop[]
}

type SequenceTramlineResolvedSegment = {
	side: FlowSideClass
	start: SequenceTramlineRelativeZone
	end: SequenceTramlineRelativeZone | 'finish'
}

type SequenceTramlineMetric = {
	type: 'transition' | 'goal'
	segments?: SequenceTramlineResolvedSegment[]
	goalSide?: FlowSideClass
}

type SequenceTramlineSignalOutcome = {
	index: number
	value: number
}

type SequenceTramlineSignalStats = {
	signal: number
	totalWeight: number
}

const sequenceTramlines: Array<{
	zone: SequenceTramlineZone
	y: number
}> = [
	{ zone: 'attack', y: 207 },
	{ zone: 'midcourt', y: 224 },
	{ zone: 'defence', y: 241 },
]

function buildSequenceYScale(cps: FlowMapDashboardData['cps']) {
	let diff = 0
	let minDiff = 0
	let maxDiff = 0

	cps.forEach(cp => {
		if (!cp.scorer) {
			return
		}
		diff += cp.scorer === 'home' ? cp.points : -cp.points
		minDiff = Math.min(minDiff, diff)
		maxDiff = Math.max(maxDiff, diff)
	})

	let domainMin = minDiff - SEQUENCE_LEAD_PADDING
	let domainMax = maxDiff + SEQUENCE_LEAD_PADDING
	const domainSpan = domainMax - domainMin

	if (domainSpan < SEQUENCE_MIN_LEAD_SPAN) {
		const midpoint = (domainMin + domainMax) / 2
		domainMin = midpoint - (SEQUENCE_MIN_LEAD_SPAN / 2)
		domainMax = midpoint + (SEQUENCE_MIN_LEAD_SPAN / 2)
	}

	const plotHeight = SEQUENCE_PLOT_BOTTOM - SEQUENCE_PLOT_TOP
	const scale = plotHeight / Math.max(domainMax - domainMin, 1)

	return {
		zeroY: SEQUENCE_PLOT_TOP + (domainMax * scale),
		scale,
	}
}

function scorewormPossessionWidth(thirdTransitions: number) {
	const baseWidth = 10
	const widthPerExtraTransition = 5
	const maxWidth = 100
	const safeTransitions = Math.max(1, thirdTransitions)

	return Math.min(maxWidth, baseWidth + ((safeTransitions - 1) * widthPerExtraTransition))
}

function tramlineColor(dominance: CourtZoneDominanceCell) {
	if (dominance.side === 'home') {
		return 'var(--flow-home)'
	}
	if (dominance.side === 'away') {
		return 'var(--flow-away)'
	}
	return '#cbd5e1'
}

function tramlineOpacity(dominance: CourtZoneDominanceCell) {
	return dominance.side ? dominance.strength : 0.18
}

function tramlineZoneLabel(zone: SequenceTramlineZone) {
	return zone === 'attack' ? 'A' : zone === 'midcourt' ? 'M' : 'D'
}

function buildSequenceLayout(dashboard: FlowMapDashboardData) {
	const bars: Array<{
		key: string
		x: number
		y: number
		width: number
		height: number
		scorer: FlowSideClass | null
		viewId: FlowMapViewId
		isSuper: boolean
	}> = [ ]
	const tramlineSamples: SequenceTramlineSample[] = [
		{
			x: 0,
			zones: emptySequenceTramlineZones(),
		},
	]
	const tramlineMetrics: SequenceTramlineMetric[] = [ ]

	const barWidth = 8
	const barCenterOffset = barWidth / 2
	const quarterGap = 10
	const barHeight = 26
	const { zeroY, scale } = buildSequenceYScale(dashboard.cps)
	const yForDiff = (value: number) => safeSvgNumber(zeroY - (value * scale), zeroY).toFixed(1)
	const quarterLabels = new Map<number, { text: string; x: number }>()
	const quarterRanges = new Map<number, { firstX: number; lastX: number }>()
	const quarterEndX = new Map<number, number>()
	const separatorByQuarter = new Map<number, number>()

	let x = 0
	let diff = 0
	let linePath = `M 0 ${yForDiff(0)}`

	dashboard.cps.forEach((cp, index) => {
		const previousQuarter = dashboard.cps[index - 1]?.quarter
		if (previousQuarter && previousQuarter !== cp.quarter) {
			const separatorX = x + (quarterGap / 2)
			x += quarterGap
			separatorByQuarter.set(previousQuarter, separatorX)
			tramlineMetrics.length = 0
			tramlineSamples.push({
				x,
				zones: emptySequenceTramlineZones(),
				breakBefore: true,
			})
		}

		if (!quarterRanges.has(cp.quarter)) {
			quarterRanges.set(cp.quarter, {
				firstX: x,
				lastX: x,
			})
		}

		const possessionStartX = x
		x += scorewormPossessionWidth(cp.thirdTransitions)
		const possessionEndX = x
		if (possessionEndX > possessionStartX) {
			tramlineSamples.push({
				x: possessionStartX,
				zones: computeSequenceTramlineZones(tramlineMetrics, dashboard),
				viewId: cp.viewId,
			})
		}
		const tramlineEvents = buildSequenceTramlineEvents(cp.row, dashboard.teams.home.code)
		tramlineEvents.forEach((eventMetric, eventIndex) => {
			tramlineMetrics.push(eventMetric)
			tramlineSamples.push({
				x: possessionStartX + ((possessionEndX - possessionStartX) * (eventIndex + 1) / tramlineEvents.length),
				zones: computeSequenceTramlineZones(tramlineMetrics, dashboard),
				viewId: cp.viewId,
			})
		})
		if (!tramlineEvents.length) {
			tramlineSamples.push({
				x: possessionEndX,
				zones: computeSequenceTramlineZones(tramlineMetrics, dashboard),
				viewId: cp.viewId,
			})
		}

		const range = quarterRanges.get(cp.quarter)
		if (range) {
			range.lastX = x + (cp.scorer ? barCenterOffset : 0)
		}

		if (!cp.scorer) {
			linePath += ` H ${safeSvgNumber(x, 0).toFixed(1)}`
			return
		}

		const barCenterX = x
		const barX = barCenterX - barCenterOffset

		bars.push({
			key: `${cp.quarter}-${index}`,
			x: barX,
			y: cp.scorer === 'home' ? 146 : 158,
			width: barWidth,
			height: barHeight,
			scorer: cp.scorer,
			viewId: cp.viewId,
			isSuper: cp.isSuper,
		})

		if (cp.scorer === 'home') {
			diff += cp.points
		} else {
			diff -= cp.points
		}

		linePath += ` H ${safeSvgNumber(barCenterX, 0).toFixed(1)} V ${yForDiff(diff)}`
	})

	dashboard.quarterNumbers.forEach(quarter => {
		const range = quarterRanges.get(quarter)
		if (!range) {
			return
		}
		quarterLabels.set(quarter, {
			text: dashboard.views[`q${quarter}`]?.buttonLabel || formatPeriodNumber(quarter, dashboard.quarterNumbers.length),
			x: (range.firstX + range.lastX) / 2,
		})
		quarterEndX.set(quarter, range.lastX)
	})

	const endX = Math.max(0, x + barCenterOffset)
	linePath += ` H ${endX}`
	const fillPath = `${linePath} V ${zeroY} H 0 Z`
	if (tramlineSamples[tramlineSamples.length - 1]?.x !== endX) {
		tramlineSamples.push({
			x: endX,
			zones: computeSequenceTramlineZones(tramlineMetrics, dashboard),
			viewId: dashboard.cps[dashboard.cps.length - 1]?.viewId,
		})
	}
	const quarterData = Array.from(quarterLabels.keys())
		.sort((a, b) => a - b)
		.map(quarter => ({
		quarter,
		label: quarterLabels.get(quarter) || { text: dashboard.views[`q${quarter}`]?.buttonLabel || formatPeriodNumber(quarter, dashboard.quarterNumbers.length), x: 0 },
		separatorX: separatorByQuarter.get(quarter) ?? null,
	}))
	const tramlineGradientRects = buildSequenceTramlineGradientRects(
		tramlineSamples,
		quarterData,
		quarterRanges,
	)
	const windows = { } as Partial<Record<FlowMapViewId, { startX: number; endX: number }>>

	quarterData.forEach(({ quarter }) => {
		const range = quarterRanges.get(quarter)
		if (!range) {
			return
		}
		const viewId = `q${quarter}` as const
		windows[viewId] = {
			startX: Math.max(0, range.firstX - barCenterOffset),
			endX: range.lastX,
		}
	})

	return {
		endX,
		zeroY,
		bars,
		linePath,
		fillPath,
		quarters: quarterData,
		quarterEndX,
		windows,
		tramlines: sequenceTramlines,
		tramlineGradientRects,
	}
}

function emptySequenceTramlineZones(): Record<SequenceTramlineZone, CourtZoneDominanceCell> {
	return {
		attack: emptyCourtZoneDominance,
		midcourt: emptyCourtZoneDominance,
		defence: emptyCourtZoneDominance,
	}
}

function computeSequenceTramlineZones(
	metrics: SequenceTramlineMetric[],
	dashboard: FlowMapDashboardData,
): Record<SequenceTramlineZone, CourtZoneDominanceCell> {
	const stats = computeSequenceTramlineSignals(
		metrics,
		sequenceExpectedAttackRate(dashboard),
		sequenceExpectedMidcourtRate(dashboard),
	)

	return {
		attack: sequenceSignalDominance(stats.attack, SEQUENCE_TRAMLINE_ATTACK_SIGNAL_DELTA),
		midcourt: sequenceSignalDominance(stats.midcourt, SEQUENCE_TRAMLINE_MIDCOURT_SIGNAL_DELTA),
		defence: sequenceSignalDominance(stats.defence, SEQUENCE_TRAMLINE_ATTACK_SIGNAL_DELTA),
	}
}

function sequenceExpectedAttackRate(dashboard: FlowMapDashboardData) {
	const attackToScore = dashboard.views.full.efficiency.attackToScore
	const combinedAttackPct = pctFromTotals(
		attackToScore.home.to + attackToScore.away.to,
		attackToScore.home.from + attackToScore.away.from,
	)

	return combinedAttackPct === null ? 0.5 : combinedAttackPct / 100
}

function sequenceExpectedMidcourtRate(dashboard: FlowMapDashboardData) {
	const midToAttack = dashboard.views.full.efficiency.midToAttack
	const combinedMidcourtPct = pctFromTotals(
		midToAttack.home.to + midToAttack.away.to,
		midToAttack.home.from + midToAttack.away.from,
	)

	return combinedMidcourtPct === null ? 0.5 : combinedMidcourtPct / 100
}

function sequenceSignalDominance(stats: SequenceTramlineSignalStats, signalDelta: number) {
	return dominanceFromDelta(
		stats.signal * signalDelta,
		'home',
		'away',
		stats.totalWeight,
	)
}

function computeSequenceTramlineSignals(
	metrics: SequenceTramlineMetric[],
	expectedAttackRate: number,
	expectedMidcourtRate: number,
): Record<SequenceTramlineZone, SequenceTramlineSignalStats> {
	const outcomes: Record<SequenceTramlineZone, SequenceTramlineSignalOutcome[]> = {
		attack: [ ],
		midcourt: [ ],
		defence: [ ],
	}
	let midcourtMetricIndex = -1
	let attackMetricIndex = -1

	metrics.forEach(metric => {
		metric.segments?.forEach(segment => {
			const reachesAttack = segment.start === 'attack'
				|| segment.end === 'attack'
				|| segment.end === 'finish'

			if (segment.start !== 'attack' && segment.end !== 'defense') {
				midcourtMetricIndex += 1
				const midcourtValue = reachesAttack
					? 1 - expectedMidcourtRate
					: -expectedMidcourtRate
				outcomes.midcourt.push({
					index: midcourtMetricIndex,
					value: segment.side === 'home' ? midcourtValue : -midcourtValue,
				})
			}

			if (reachesAttack) {
				attackMetricIndex += 1
				const attackValue = segment.end === 'finish'
					? 1 - expectedAttackRate
					: -expectedAttackRate
				if (segment.side === 'home') {
					outcomes.attack.push({
						index: attackMetricIndex,
						value: attackValue,
					})
				} else {
					outcomes.defence.push({
						index: attackMetricIndex,
						value: -attackValue,
					})
				}
			}
		})
	})

	return {
		attack: computeSequenceTramlineSignalStats(outcomes.attack, attackMetricIndex + 1),
		midcourt: computeSequenceTramlineSignalStats(outcomes.midcourt, midcourtMetricIndex + 1),
		defence: computeSequenceTramlineSignalStats(outcomes.defence, attackMetricIndex + 1),
	}
}

function computeSequenceTramlineSignalStats(
	outcomes: SequenceTramlineSignalOutcome[],
	metricCount: number,
): SequenceTramlineSignalStats {
	const lastIndex = metricCount - 1
	let weightedSignal = 0

	outcomes.forEach(outcome => {
		const weight = 0.5 ** ((lastIndex - outcome.index) / SEQUENCE_TRAMLINE_DECAY_HALF_LIFE)
		weightedSignal += outcome.value * weight
	})

	return {
		signal: clamp(weightedSignal, -1, 1),
		totalWeight: outcomes.length,
	}
}

function buildSequenceTramlineEvents(row: FlowMapParsedRow, homeCode: string): SequenceTramlineMetric[] {
	const events: SequenceTramlineMetric[] = [ ]
	let position = 1
	let holderSide: FlowSideClass = row.cpTeamCode === homeCode ? 'home' : 'away'
	let segmentStart: SequenceTramlineRelativeZone = 'mid'

	let turnoverIndex = 0
	while (turnoverIndex < row.turnovers.length) {
		const turnover = row.turnovers[turnoverIndex]
		const nextTurnover = row.turnovers[turnoverIndex + 1]
		const targetPosition = sequenceRawZoneToIndex(turnover)

		if (isSequenceOffensiveGainPair(turnover, nextTurnover)) {
			pushSequenceTransitionEvents(events, position, targetPosition)
			position = targetPosition
			turnoverIndex += 2
			continue
		}

		const end = sequenceRelativeZone(holderSide, turnover)
		pushSequenceTransitionEvents(events, position, targetPosition, {
			side: holderSide,
			start: segmentStart,
			end,
		})
		position = targetPosition
		holderSide = holderSide === 'home' ? 'away' : 'home'
		segmentStart = inverseSequenceRelativeZone(end)
		turnoverIndex += 1
	}

	if (row.scorerCode) {
		const scorerSide: FlowSideClass = row.scorerCode === homeCode ? 'home' : 'away'
		const goalPosition = scorerSide === 'home' ? 2 : 0
		pushSequenceTransitionEvents(events, position, goalPosition)
		events.push({
			type: 'goal',
			segments: [ {
				side: scorerSide,
				start: segmentStart,
				end: 'finish',
			} ],
			goalSide: scorerSide,
		})
	} else if (!row.isInProgress) {
		addSegmentToLastSequenceTramlineEvent(events, {
			side: holderSide,
			start: segmentStart,
			end: 'defense',
		})
	}

	return events
}

function pushSequenceTransitionEvents(
	events: SequenceTramlineMetric[],
	startPosition: number,
	targetPosition: number,
	resolvedSegment?: SequenceTramlineResolvedSegment,
) {
	if (startPosition === targetPosition) {
		if (resolvedSegment) {
			events.push({
				type: 'transition',
				segments: [ resolvedSegment ],
			})
			return 1
		}
		return 0
	}

	const step = targetPosition > startPosition ? 1 : -1
	let count = 0
	for (let position = startPosition + step; ; position += step) {
		const isFinalTransition = position === targetPosition
		events.push({
			type: 'transition',
			...isFinalTransition && resolvedSegment ? { segments: [ resolvedSegment ] } : { },
		})
		count += 1
		if (isFinalTransition) {
			break
		}
	}
	return count
}

function addSegmentToLastSequenceTramlineEvent(
	events: SequenceTramlineMetric[],
	segment: SequenceTramlineResolvedSegment,
) {
	const lastEvent = events[events.length - 1]
	if (!lastEvent) {
		return
	}
	lastEvent.segments = [ ...(lastEvent.segments || [ ]), segment ]
}

function isSequenceOffensiveGainPair(
	firstTurnover: FlowMapParsedRow['turnovers'][number] | undefined,
	secondTurnover: FlowMapParsedRow['turnovers'][number] | undefined,
) {
	return !!firstTurnover
		&& firstTurnover === secondTurnover
		&& firstTurnover !== 'M'
}

function sequenceRawZoneToIndex(zone: FlowMapParsedRow['turnovers'][number]) {
	return zone === 'D' ? 0 : zone === 'M' ? 1 : 2
}

function sequenceRelativeZone(
	side: FlowSideClass,
	zone: FlowMapParsedRow['turnovers'][number],
): SequenceTramlineRelativeZone {
	if (zone === 'M') {
		return 'mid'
	}
	if (side === 'home') {
		return zone === 'D' ? 'defense' : 'attack'
	}
	return zone === 'A' ? 'defense' : 'attack'
}

function inverseSequenceRelativeZone(zone: SequenceTramlineRelativeZone | 'finish'): SequenceTramlineRelativeZone {
	if (zone === 'defense') {
		return 'attack'
	}
	if (zone === 'attack') {
		return 'defense'
	}
	return 'mid'
}

function buildSequenceTramlineGradientRects(
	samples: SequenceTramlineSample[],
	quarterData: Array<{ quarter: number }>,
	quarterRanges: Map<number, { firstX: number; lastX: number }>,
): SequenceTramlineGradientRect[] {
	return quarterData.flatMap(({ quarter }) => {
		const range = quarterRanges.get(quarter)
		if (!range || range.lastX <= range.firstX) {
			return [ ]
		}

		return sequenceTramlines.map(line => {
			const width = range.lastX - range.firstX
			const stops: SequenceTramlineGradientStop[] = [ ]
			const addStop = (x: number, dominance: CourtZoneDominanceCell) => {
				const offset = clamp((x - range.firstX) / width, 0, 1)
				const previous = stops[stops.length - 1]
				if (previous && Math.abs(previous.offset - offset) < 0.0001) {
					previous.dominance = dominance
					return
				}
				stops.push({ offset, dominance })
			}

			for (let index = 1; index < samples.length; index += 1) {
				const start = samples[index - 1]
				const end = samples[index]
				if (end.x <= start.x
					|| end.breakBefore
					|| end.x <= range.firstX
					|| start.x >= range.lastX) {
					continue
				}

				addStop(Math.max(start.x, range.firstX), start.zones[line.zone])
				addStop(Math.min(end.x, range.lastX), end.zones[line.zone])
			}

			if (!stops.length) {
				stops.push(
					{ offset: 0, dominance: emptyCourtZoneDominance },
					{ offset: 1, dominance: emptyCourtZoneDominance },
				)
			}
			if (stops[0].offset > 0) {
				stops.unshift({ offset: 0, dominance: stops[0].dominance })
			}
			if (stops[stops.length - 1].offset < 1) {
				stops.push({ offset: 1, dominance: stops[stops.length - 1].dominance })
			}

			return {
				key: `${line.zone}-${quarter}`,
				zone: line.zone,
				x: range.firstX,
				y: line.y - (SEQUENCE_TRAMLINE_THICKNESS / 2),
				width,
				height: SEQUENCE_TRAMLINE_THICKNESS,
				viewId: `q${quarter}` as const,
				stops,
			}
		})
	})
}

const ChainBadge = (props: {
	badge: FlowMapChainBadge
	className?: string
	style?: React.CSSProperties
}) => {
	const { badge, className, style } = props

	const label = badge.type === 'cp' ? 'M'
		: badge.type === 'zone' ? badge.zone
        : badge.type === 'goal' ? 'GOAL'
        : badge.type === 'super' ? '2-GOAL'
		: '—'

	const isTerminal = badge.type === 'goal' || badge.type === 'super' || badge.type === 'miss'

	const normalizedLabel = normalizeChainLabel(label, badge.side)

	return (
		<span className={[
			'chain-badge',
			`chain-badge-${badge.side}`,
			`chain-badge-${badge.type}`,
			`chain-badge-${label}`,
			isTerminal ? 'chain-badge-end' : '',
			className || '',
		].filter(Boolean).join(' ')}
		style={style}
		>
			{normalizedLabel}
		</span>
	)
}

function normalizeChainLabel(label: string|undefined, side: FlowSideClass) {
	if (side === 'away') {
		if (label === 'D') return 'A'
		if (label === 'A') return 'D'
	}
	return label
}

const FLOW_CHAIN_APPEND_ANIMATION_MS = 240
const FLOW_CHAIN_CHILD_FADE_MS = 180
const FLOW_CHAIN_CHILD_STAGGER_MS = 70

type FlowChainRenderItem = {
	key: string
	type: 'arrow' | 'badge'
	badge?: FlowMapChainBadge
}

function buildFlowChainRenderItems(chain: FlowMapChain): FlowChainRenderItem[] {
	const items: FlowChainRenderItem[] = [ ]
	chain.badges.forEach((badge, index) => {
		if (index > 0) {
			items.push({
				key: `arrow-${index}`,
				type: 'arrow',
			})
		}
		items.push({
			key: `badge-${index}`,
			type: 'badge',
			badge,
		})
	})
	return items
}

const ScoreChainRow = (props: {
	chain: FlowMapChain
	isNewQuarter: boolean
	animateInitialItems: boolean
}) => {
	const { chain, isNewQuarter, animateInitialItems } = props
	const previousItemCountRef = useRef<number | null>(null)
	const items = useMemo(() => buildFlowChainRenderItems(chain), [ chain ])
	const previousItemCount = previousItemCountRef.current
	const enteringStartIndex = animateInitialItems
		? 0
		: previousItemCount !== null && items.length > previousItemCount
			? previousItemCount
			: items.length

	useLayoutEffect(() => {
		previousItemCountRef.current = items.length
	}, [ items.length ])

	return (
		<React.Fragment>
			{isNewQuarter && (
				<div className="chain-quarter-divider" />
			)}
			<div className={`flow-chain-row ${chain.scorer === chain.cpSide ? 'chain-converted' : 'chain-lost'}`}>
				<span className="chain-score-label">{chain.scoreLabel}</span>
				<div className="chain-badges">
					{items.map((item, itemIndex) => {
						const isEntering = itemIndex >= enteringStartIndex
						const animationStyle = isEntering
							? ({
								'--flow-chain-child-fade-ms': `${FLOW_CHAIN_CHILD_FADE_MS}ms`,
								'--flow-chain-enter-delay': `${(itemIndex - enteringStartIndex) * FLOW_CHAIN_CHILD_STAGGER_MS}ms`,
							} as React.CSSProperties)
							: undefined
						const itemClassName = [
							'flow-chain-child',
							isEntering ? 'flow-chain-child-entering' : '',
						].filter(Boolean).join(' ')

						if (item.type === 'arrow') {
							return (
								<span
									key={item.key}
									className={`chain-arrow ${itemClassName}`}
									style={animationStyle}
								>
									›
								</span>
							)
						}

						return (
							<ChainBadge
								key={item.key}
								badge={item.badge!}
								className={itemClassName}
								style={animationStyle}
							/>
						)
					})}
				</div>
			</div>
		</React.Fragment>
	)
}

const ScoreChains = (props: {
	dashboard: FlowMapDashboardData
	selectedViewId: FlowMapViewId
	enableLiveAnimations: boolean
	onHelpClick: (section: HelpInsightSection) => void
}) => {
	const { dashboard, selectedViewId, enableLiveAnimations, onHelpClick } = props

	const isFullGame = selectedViewId === 'full'
	const chains = isFullGame
		? dashboard.chains
		: dashboard.chains.filter(c => c.viewId === selectedViewId)
	const [ isExpanded, setIsExpanded ] = useState(false)
	const chainsListRef = useRef<HTMLDivElement | null>(null)
	const previousChainCountRef = useRef<number | null>(null)
	const previousHeightRef = useRef(0)
	const previousSelectedViewRef = useRef<FlowMapViewId | null>(null)
	const animationRef = useRef<Animation | null>(null)
	const previousChainCount = previousChainCountRef.current
	const hasAppendedRows = (
		enableLiveAnimations &&
		previousSelectedViewRef.current === selectedViewId &&
		previousChainCount !== null &&
		chains.length > previousChainCount
	)
	const appendedRowStartIndex = hasAppendedRows
		? previousChainCount
		: chains.length

	useEffect(() => {
		return () => {
			animationRef.current?.cancel()
		}
	}, [ ])

	useEffect(() => {
		setIsExpanded(false)
	}, [ selectedViewId ])

	useLayoutEffect(() => {
		const list = chainsListRef.current
		if (!list) {
			return
		}

		animationRef.current?.cancel()
		animationRef.current = null
		list.classList.remove('flow-chains-list-entering')

		const currentHeight = list.scrollHeight
		const previousChainCount = previousChainCountRef.current
		const isAppendOnlyUpdate = (
			enableLiveAnimations &&
			previousSelectedViewRef.current === selectedViewId &&
			previousChainCount !== null &&
			previousChainCount > 0 &&
			chains.length > previousChainCount
		)

		if (isAppendOnlyUpdate && previousHeightRef.current > 0) {
			const addedHeight = currentHeight - previousHeightRef.current
			if (addedHeight > 0) {
				list.classList.add('flow-chains-list-entering')
				const animation = list.animate([
					{ transform: `translateY(${-addedHeight}px)` },
					{ transform: 'translateY(0px)' },
				], {
					duration: FLOW_CHAIN_APPEND_ANIMATION_MS,
					easing: 'ease-out',
				})
				animationRef.current = animation
				const clearAnimationState = () => {
					if (animationRef.current === animation) {
						animationRef.current = null
					}
					list.classList.remove('flow-chains-list-entering')
				}
				animation.addEventListener('finish', clearAnimationState, { once: true })
				animation.addEventListener('cancel', clearAnimationState, { once: true })
			}
		}

		previousChainCountRef.current = chains.length
		previousHeightRef.current = currentHeight
		previousSelectedViewRef.current = selectedViewId
	}, [ chains.length, enableLiveAnimations, selectedViewId ])

	return (
		<section
			className={`flow-card flow-card-chains${isExpanded ? ' expanded' : ''}`}
			role="button"
			tabIndex={0}
			aria-expanded={isExpanded}
			onClick={() => setIsExpanded(value => !value)}
			onKeyDown={event => {
				if (event.key === 'Enter' || event.key === ' ') {
					event.preventDefault()
					setIsExpanded(value => !value)
				}
			}}
		>
			<div className="flow-card-body">
                <div className="flow-section-label">
                    Chains
					<FlowSectionHelpIcon
						helpSection="chains"
						onHelpClick={onHelpClick}
					/>
                </div>
				<div className="flow-chains-window">
					<div className="flow-chains-list" ref={chainsListRef}>
						{chains.map((chain, i) => {
							const prevQuarter = i > 0 ? chains[i - 1].quarter : null
							const isNewQuarter = isFullGame && prevQuarter !== null && chain.quarter !== prevQuarter
							return (
								<ScoreChainRow
									key={`${selectedViewId}:${i}:${chain.quarter}:${chain.scoreLabel}`}
									chain={chain}
									isNewQuarter={isNewQuarter}
									animateInitialItems={enableLiveAnimations && i >= appendedRowStartIndex}
								/>
							)
						})}
					</div>
				</div>
			</div>
		</section>
	)
}

export default InsightDashboard
